import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as nebulaModes from '@nebula.gl/edit-modes';
import EditableCartoGeoJsonLayer from '@carto/react-widgets/src/layers/EditableCartoGeoJsonLayer';
import useEventManager from '@carto/react-widgets/src/layers/useEventManager';
import { hexToRgb, useTheme } from '@mui/material';
import {
  setFeatureSelectionEnabledForLine,
  addSpatialFilterForLine,
  addLassoSelection,
  clearClusterSelection,
  clearLassoSelection,
} from 'store/carto/reducer';
import { getCartoSelectionForLineId } from 'store/carto/selectors';
import { minBy } from 'lodash';

const { ViewMode } = nebulaModes;

const formatRGBA = (cssRgba) => {
  const [r, g, b, alpha] = cssRgba.replace(/[rgba() ]/g, '').split(',');
  return [r, g, b, alpha ? alpha * 255 : 255].map(Number);
};

export const useFeatureSelectionLayer = (
  lineId,
  options,
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const { isEnabled, eventManager } = {
    isEnabled: true,
    eventManager: null,
    mask: false,
    ...options,
  };

  const dispatch = useDispatch();
  const theme = useTheme();
  const { spatialFilters, featureSelectionMode, featureSelectionEnabled } = useSelector(
    getCartoSelectionForLineId(lineId),
  );

  const [selectedFeatureIndex, setSelectedFeatureIndex] = useState(null);

  const hasGeometry = !!spatialFilters?.length;
  const isSelected = selectedFeatureIndex !== null;

  const customEventManager = useEventManager({
    eventManager,
    isEdit: false,
    isSelected,
    featureSelectionMode,
  });

  useEffect(() => {
    if (isSelected || !spatialFilters?.length) {
      setSelectedFeatureIndex(null);
    }
  }, [spatialFilters, isSelected]);

  const mode = useMemo(() => {
    if (featureSelectionEnabled && featureSelectionMode && !isSelected) {
      return nebulaModes[featureSelectionMode];
    }
    return ViewMode;
  }, [isSelected, featureSelectionMode, featureSelectionEnabled]);

  const primaryAsRgba = useMemo(() => formatRGBA(hexToRgb(theme.palette.primary.main)), [theme]);
  const secondaryAsRgba = useMemo(() => formatRGBA(hexToRgb(theme.palette.secondary.main)), [theme]);
  const mainColor = hasGeometry && !isSelected ? secondaryAsRgba : primaryAsRgba;

  const handleLassoSelection = (coordinates, polygonId) => {
    if (!coordinates?.length) return;

    const leftCoordinate = minBy(coordinates[0], (coord) => coord[0]);
    dispatch(clearClusterSelection());
    dispatch(addLassoSelection({ anchor: leftCoordinate, polygonId }));
  };

  return useMemo(() => {
    if (!isEnabled) return [];

    const layers = [];

    if (featureSelectionEnabled || spatialFilters?.length) {
      layers.push(
        new EditableCartoGeoJsonLayer({
          eventManager: customEventManager,
          id: `FeatureSelectionLayer-${lineId}`,
          billboard: false,
          pickable: true,
          data: {
            type: 'FeatureCollection',
            features: spatialFilters,
          },
          dataComparator: (data, oldData) => data.features[0] === oldData.features[0],
          mode,
          selectedFeatureIndexes: Number.isFinite(selectedFeatureIndex) ? [selectedFeatureIndex] : [],
          onEdit: ({ updatedData, editType }) => {
            if (!featureSelectionEnabled) {
              return;
            }

            if (editType === 'addFeature') {
              dispatch(setFeatureSelectionEnabledForLine({ lineId, enabled: false }));
            }

            if (updatedData.features.length !== 0 && !editType.includes('Tentative')) {
              const [lastFeature] = updatedData.features.slice(-1);

              if (lastFeature?.geometry?.coordinates) {
                dispatch(clearLassoSelection());
                handleLassoSelection(lastFeature.geometry.coordinates, null);
                dispatch(
                  addSpatialFilterForLine({
                    lineId,
                    spatialFilter: { ...lastFeature, polygonId: null },
                  }),
                );
              }
            }
          },
          onClick: ({ index, object }) => {
            if (featureSelectionEnabled && featureSelectionMode) return;

            if (index < spatialFilters.length && object?.polygonId && object?.geometry?.type !== 'Point') {
              dispatch(clearLassoSelection());
              dispatch(clearClusterSelection());

              handleLassoSelection(object.geometry.coordinates, object.polygonId);
            }
          },
          getLineColor: mainColor,
          getFillColor: [...mainColor.slice(0, 3), 255 * 0.1],
          getTentativeFillColor: [...primaryAsRgba.slice(0, 3), 255 * 0.08],
          getTentativeLineColor: primaryAsRgba,
          getEditHandlePointColor: [0xff, 0xff, 0xff],
          getEditHandlePointOutlineColor: primaryAsRgba,
          editHandlePointStrokeWidth: 5,
          getEditHandlePointRadius: 2,
          getLineWidth: 4,
        }),
      );
    }

    return layers;
  }, [
    lineId,
    spatialFilters,
    featureSelectionMode,
    featureSelectionEnabled,
    selectedFeatureIndex,
    mode,
    mainColor,
    primaryAsRgba,
    customEventManager,
    dispatch,
  ]);
};
